import CheckIcon from '@mui/icons-material/Check'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import get from 'lodash/get'
import reduce from 'lodash/reduce'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import eyedrop from '@/assets/images/enamelEyeDrop.svg'
import Button from '@/components/Button'
import SnackbarTooltip from '@/components/SnackbarTooltip'
import { imageType } from '@/pages/getRecipe/allInOne/utils/constants'
import { useAppSelector } from '@/store'
import type { LABColor } from '@/store/appSlice'
import { PLACEMENT } from '@/store/appSlice'
import { setSelectedColorCheckerKey } from '@/store/digitalTryInSlice'
import { DIGITAL_REGION } from '@/utils/constants'
import { CIELABdeltaE, CIELABdeltaE94 } from '@/utils/helper'

const DEFAULT_BG =
  'linear-gradient(94deg, #FF6339 5.33%, #ABFA59 24.29%, #0023F6 45.5%, #FF6339 64.9%, #BBF959 82.5%, #0025EA 99.2%);'

const ValueBox = ({
  label = '',
  value,
}: {
  label: string
  value: number | null
}) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      // gap={0.5}
      flexGrow={1}
      lineHeight="normal"
      gap={'6px'}
    >
      {label}:
      <Box
        flexGrow={1}
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {value || '-'}
      </Box>
    </Box>
  )
}

const PickerIcon = ({
  placement,
  region,
  onPick,
  setForceActive,
}: {
  placement: PLACEMENT
  region: DIGITAL_REGION
  onPick?: () => void
  setForceActive?: (activate: boolean) => void
}) => {
  const dispatch = useDispatch()
  const selectedColorCheckerKey = useAppSelector(
    (store) => store.digitalTryIn.selectedColorCheckerKey,
  )

  const isActive = selectedColorCheckerKey?.[placement] === region
  return (
    <Button
      variant="outlined"
      size="small"
      label={
        isActive ? (
          <CheckIcon fontSize="small" />
        ) : (
          <img src={eyedrop} alt="Select" width={18} height={18} />
        )
      }
      sx={{
        alignSelf: 'flex-start',
        fontSize: '16px',
        padding: '8px',
        cursor: 'pointer',
        lineHeight: 'normal',
        height: '36px',
        width: '36px',
        minWidth: '36px',
        borderRadius: '4px',
      }}
      textTransform="uppercase"
      onClick={() => {
        dispatch(
          setSelectedColorCheckerKey({
            [PLACEMENT.LEFT]: region,
            [PLACEMENT.RIGHT]: region,
          }),
        )
        setForceActive?.(true)
        onPick?.()
      }}
    />
  )
}

const ColorShade = ({
  reverse = false,
  placement,
  region,
}: {
  reverse?: boolean
  placement: PLACEMENT
  region: DIGITAL_REGION
}) => {
  const selectedColorCheckerShade = useAppSelector(
    (store) => store.digitalTryIn.selectedColorCheckerShade,
  )

  const valuesMap = selectedColorCheckerShade?.[placement]?.[region]

  const labToString = (values?: LABColor) => {
    if (!values?.L || !values?.a || !values?.b) return null

    return `lab(${Math.round(Number(values.L))}% ${Math.round(
      Number(values.a),
    )} ${Math.round(Number(values.b))})`
  }

  const background = labToString(valuesMap)
    ? labToString(valuesMap)
    : DEFAULT_BG

  const isAbove1920 = useMediaQuery('(min-width:1920px)')
  const isAbove1440 = useMediaQuery('(min-width:1440px)')

  return (
    <>
      <Box
        display="flex"
        flexGrow={1}
        height="20px"
        sx={{
          background,
          ...(background === DEFAULT_BG
            ? {
                backgroundSize: '200% 100%',
                backgroundPosition: reverse ? 'right' : 'left',
              }
            : {}),
          borderRadius: reverse ? '0 50px 50px 0' : '50px 0 0 50px',
        }}
      />
      {isAbove1440 && (
        <Box
          position="absolute"
          display="flex"
          flexGrow={1}
          width="calc(50% - 35px)"
          top={40}
          {...(reverse ? { right: 0 } : { left: 0 })}
          justifyContent="space-between"
          flexDirection={isAbove1920 ? 'row' : 'column'}
          bgcolor="#292929"
          borderRadius="4px"
          padding="4px 6px"
          gap="2px"
        >
          {Object.keys(valuesMap || { L: null, a: null, b: null })?.map(
            (key) => (
              <ValueBox
                key={key}
                label={key || ''}
                value={
                  valuesMap?.[key as keyof typeof valuesMap]
                    ? Math.round(
                        Number(valuesMap[key as keyof typeof valuesMap]),
                      )
                    : null
                }
              />
            ),
          )}
        </Box>
      )}
    </>
  )
}

const DeltaContainer = ({
  placement,
  region,
}: {
  placement: PLACEMENT
  region: DIGITAL_REGION
}) => {
  const theme = useTheme()
  const [delta, setDelta] = useState<number>(0)
  const [border, setBorder] = useState<string>('#999')

  const selectedColorCheckerShade = useAppSelector(
    (store) => store.digitalTryIn.selectedColorCheckerShade,
  )

  const dualCanvasImage = useAppSelector((store) => store.app.dualCanvasImage)
  const isDualCanvasImage = useMemo(() => {
    return (
      dualCanvasImage?.[PLACEMENT.LEFT] && dualCanvasImage?.[PLACEMENT.RIGHT]
    )
  }, [dualCanvasImage])

  const canvasImageType = useMemo(() => {
    return placement !== PLACEMENT.CENTER
      ? dualCanvasImage?.[placement]?.type
      : null
  }, [dualCanvasImage, placement])

  const transformDict = useMemo(() => {
    return reduce(
      [DIGITAL_REGION.CERVICAL, DIGITAL_REGION.INCISAL, DIGITAL_REGION.MIDDLE],
      (result, key) => {
        const leftValues = get(selectedColorCheckerShade, `left.${key}`, {})
        const rightValues = get(selectedColorCheckerShade, `right.${key}`, {})

        result[key as DIGITAL_REGION] = {
          left: Object.values(leftValues).map(Number),
          right: Object.values(rightValues).map(Number),
        }
        return result
      },
      {} as Record<DIGITAL_REGION, { left: number[]; right: number[] }>,
    )
  }, [selectedColorCheckerShade])
  useEffect(() => {
    if (transformDict?.[region]?.left && transformDict?.[region]?.right) {
      if (
        isDualCanvasImage &&
        (canvasImageType === imageType.RAW ||
          canvasImageType === imageType.IMAGE)
      ) {
        const deltaValue = CIELABdeltaE(
          transformDict[region].left,
          transformDict[region].right,
        )
        setDelta(Number(deltaValue))
      } else if (isDualCanvasImage && canvasImageType === imageType.OPTISHADE) {
        const deltaValue = CIELABdeltaE94(
          transformDict[region].left,
          transformDict[region].right,
        )
        setDelta(Number(deltaValue))
      }
    } else {
      setDelta(0)
    }
  }, [transformDict, region, isDualCanvasImage, canvasImageType])

  useEffect(() => {
    if (transformDict?.[region]?.left && transformDict?.[region]?.right) {
      if (delta >= 0 && delta < 1.5) {
        setBorder('#68CEA1')
      } else if (delta >= 1.5 && delta < 2.5) {
        setBorder('#2B66F6')
      } else if (delta >= 2.5 && delta < 3) {
        setBorder('#FFB975')
      } else if (delta > 3) {
        setBorder('#E46962')
      } else {
        setBorder('#999')
      }
    } else {
      setBorder('#999')
    }
  }, [delta, transformDict])

  return (
    <Box
      height="30px"
      width="63px"
      display="flex"
      justifyItems="center"
      alignItems="center"
      sx={{
        borderRadius: '8px',
        background: theme.palette.background.paper,
        border: `1px solid ${border}`,
      }}
      margin="auto"
    >
      <Box
        display="flex"
        flexGrow={1}
        alignItems="center"
        justifyContent="center"
        fontSize="12px"
        fontWeight={400}
        color={'#D9D9D9'}
      >
        {delta ? `DE*${delta}` : '_ _'}
      </Box>
    </Box>
  )
}

interface ColorCheckerContainerProps {
  region: DIGITAL_REGION
  containerRef: React.RefObject<HTMLDivElement>
  setForceActive?: (activate: boolean) => void
}

const ColorCheckerContainer: React.FC<ColorCheckerContainerProps> = ({
  region,
  containerRef,
  setForceActive,
}) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const isBelowOrEqual1024 = useMediaQuery('(max-width:1024px)')

  const selectedColorCheckerShade = useAppSelector(
    (store) => store.digitalTryIn.selectedColorCheckerShade,
  )

  const valuesMapLeft = selectedColorCheckerShade?.[PLACEMENT.LEFT]?.[region]
  const valuesMapRight = selectedColorCheckerShade?.[PLACEMENT.RIGHT]?.[region]

  useEffect(() => {
    if (valuesMapLeft || valuesMapRight) {
      setSnackbarOpen(false)
    }
  }, [valuesMapLeft, valuesMapRight])

  const renderPickerWithValues = (placement: PLACEMENT) => (
    <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
      <PickerIcon
        placement={placement}
        region={region}
        onPick={() => setSnackbarOpen(true)}
        setForceActive={setForceActive}
      />
      {isBelowOrEqual1024 && (
        <Box
          bgcolor="#292929"
          borderRadius="4px"
          padding="4px 6px"
          display="flex"
          flexDirection="column"
          gap="2px"
          mt={0.5}
        >
          {Object.keys(
            selectedColorCheckerShade?.[placement]?.[region] || {
              L: null,
              a: null,
              b: null,
            },
          )?.map((key) => (
            <ValueBox
              key={key}
              label={key || ''}
              value={
                selectedColorCheckerShade?.[placement]?.[region]?.[
                  key as keyof LABColor
                ]
                  ? Math.round(
                      Number(
                        selectedColorCheckerShade?.[placement]?.[region]?.[
                          key as keyof LABColor
                        ],
                      ),
                    )
                  : null
              }
            />
          ))}
        </Box>
      )}
    </Box>
  )

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      gap={1}
      flexGrow={1}
      minHeight="100px"
    >
      {renderPickerWithValues(PLACEMENT.LEFT)}

      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexGrow={1}
        position="relative"
      >
        <ColorShade placement={PLACEMENT.LEFT} region={region} />
        <DeltaContainer placement={PLACEMENT.LEFT} region={region} />
        <ColorShade reverse placement={PLACEMENT.RIGHT} region={region} />
      </Box>

      {renderPickerWithValues(PLACEMENT.RIGHT)}

      <SnackbarTooltip
        containerRef={containerRef}
        message="Position the cursor over the area you want to measure and adjust the size as needed."
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        placement="top"
        offset={[0, -60]}
        maxWidth={600}
        autoHideDuration={6000}
      />
    </Box>
  )
}

export default ColorCheckerContainer
